import React, { useEffect, useMemo, useRef, useState } from "react";
import { http, fmtErr, WS_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Package, ScanLine, Truck, Coins, Bell, GemIcon, TrendingUp } from "lucide-react";

const STATUS_STYLE = {
  rezerve:       "bg-amber-500/10 text-amber-400 ring-amber-500/30",
  hazirlandi:    "bg-sky-500/10 text-sky-400 ring-sky-500/30",
  teslim_edildi: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/30",
  iptal:         "bg-red-500/10 text-red-400 ring-red-500/30",
};

export default function JewelerPanel() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [prices, setPrices] = useState({ gram_price_kurus: 260000, workmanship_bps: 800 });
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("orders");
  const [prepareVals, setPrepareVals] = useState({}); // {resId: barcode}
  const [deliverVals, setDeliverVals] = useState({}); // {resId: code}
  const wsRef = useRef(null);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const [m, o, s, p, sm] = await Promise.all([
        http.get("/jeweler/me"),
        http.get("/jeweler/me/orders"),
        http.get("/jeweler/me/stock"),
        http.get("/jeweler/me/prices"),
        http.get("/jeweler/me/summary"),
      ]);
      setMe(m.data); setOrders(o.data); setStock(s.data);
      setPrices({ gram_price_kurus: p.data.gram_price_kurus, workmanship_bps: p.data.workmanship_bps });
      setSummary(sm.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!me?.jeweler?.id) return;
    const token = localStorage.getItem("token");
    const ws = new WebSocket(`${WS_BASE}/api/ws/jewelers/${me.jeweler.id}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(e.data);
        if (m.type === "new_order") {
          toast.success("Yeni sipariş geldi", { description: `${m.reservation.product_type} · ${fmtGold(m.reservation.grams_mg)}` });
          try { audioRef.current?.play(); } catch { /* noop */ }
          load();
        }
      } catch { /* noop */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [me?.jeweler?.id]);

  const prepare = async (id) => {
    const bar = (prepareVals[id] || "").trim();
    if (!bar) return toast.error("Barkod girin");
    try {
      const { data } = await http.post(`/jeweler/me/orders/${id}/prepare`, { barcode: bar });
      toast.success(`Hazırlandı — Teslim kodu: ${data.delivery_code}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const deliver = async (id) => {
    const code = (deliverVals[id] || "").trim();
    if (!code) return toast.error("Teslim kodunu girin");
    try {
      await http.post(`/jeweler/me/orders/${id}/deliver`, { delivery_code: code });
      toast.success("Teslim edildi");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const stockDelta = async (product_type, delta) => {
    try {
      await http.post("/jeweler/me/stock", { product_type, delta_count: delta });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const savePrices = async () => {
    try {
      await http.post("/jeweler/me/prices", {
        gram_price_kurus: parseInt(prices.gram_price_kurus, 10),
        workmanship_bps: parseInt(prices.workmanship_bps, 10),
      });
      toast.success("Fiyat güncellendi");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!user || user.role !== "jeweler") return <div className="mx-auto max-w-7xl px-4 py-10 text-muted-foreground">Kuyumcu yetkisi gerekli.</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      {/* Audio bell */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRlAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=" preload="auto" />

      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary/80">{t.jeweler}</div>
          <h1 className="font-serif text-4xl gold-text">{me?.jeweler?.name || "..."}</h1>
          <p className="text-sm text-muted-foreground">{me?.jeweler?.city} · Komisyon {(me?.jeweler?.commission_bps || 0) / 100}%</p>
        </div>
        <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40 max-w-xs">
          {t.marketplace_note}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {[
          [t.orders_today, summary?.orders ?? "-", Package],
          [t.grams_today, fmtGold(summary?.grams_mg || 0), GemIcon],
          [t.total_cash, fmtTRY(summary?.total_kurus || 0), Coins],
          [t.commission,  fmtTRY(summary?.commission_kurus || 0), TrendingUp],
          [t.payable, fmtTRY(summary?.payable_kurus || 0), TrendingUp],
        ].map(([label, val, Icon], i) => (
          <Card key={label} className="p-4" data-testid={`jeweler-stat-${i}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="w-3 h-3" /> {label}</div>
            <div className="font-mono text-xl mt-1">{val}</div>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 max-w-md">
          <TabsTrigger value="orders" data-testid="jw-tab-orders" className="gap-2"><Bell className="w-3 h-3" /> {t.orders}</TabsTrigger>
          <TabsTrigger value="stock" data-testid="jw-tab-stock">{t.stock}</TabsTrigger>
          <TabsTrigger value="prices" data-testid="jw-tab-prices">{t.prices}</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-5 space-y-3">
          {orders.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Sipariş yok.</Card>}
          {orders.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`jw-order-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-primary/70">{r.product_type} · {fmtGold(r.grams_mg)}</div>
                  <div className="font-serif text-xl">{r.event_title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtDate(r.starts_at)} · {r.display_name}</div>
                </div>
                <div className="text-end">
                  <Badge className={`${STATUS_STYLE[r.status]} ring-1 uppercase tracking-wider text-[10px]`}>{t["status_" + r.status] || r.status}</Badge>
                  <div className="font-mono text-sm mt-2">{fmtTRY(r.locked_price_kurus * r.grams_mg / 1000)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.lock_ref}: {r.lock_ref}</div>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {r.status === "rezerve" && (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1"><Label className="text-xs">{t.barcode}</Label><Input value={prepareVals[r.id] || ""} onChange={(e) => setPrepareVals({ ...prepareVals, [r.id]: e.target.value })} placeholder="BAR-1234" data-testid={`jw-barcode-${r.id}`} /></div>
                    <Button onClick={() => prepare(r.id)} className="rounded-full gap-2" data-testid={`jw-prepare-${r.id}`}><ScanLine className="w-4 h-4" /> {t.prepare}</Button>
                  </div>
                )}
                {r.status === "hazirlandi" && (
                  <>
                    <div className="p-3 rounded-lg bg-primary/10 ring-1 ring-primary/30 text-center">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.delivery_code}</div>
                      <div className="font-mono text-xl gold-text">{r.delivery_code}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Çift bu kodu göstermeli</div>
                    </div>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1"><Label className="text-xs">{t.scan_code}</Label><Input value={deliverVals[r.id] || ""} onChange={(e) => setDeliverVals({ ...deliverVals, [r.id]: e.target.value })} placeholder="ABC123" data-testid={`jw-code-${r.id}`} /></div>
                      <Button onClick={() => deliver(r.id)} className="rounded-full gap-2" data-testid={`jw-deliver-${r.id}`}><Truck className="w-4 h-4" /> {t.deliver}</Button>
                    </div>
                  </>
                )}
                {r.status === "teslim_edildi" && (
                  <div className="text-xs text-emerald-400">Teslim edildi: {fmtDate(r.delivered_at)} · Barkod: {r.barcode}</div>
                )}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="stock" className="mt-5">
          <Card className="p-5">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-start py-2">Ürün</th><th>Gram (mg)</th><th>{t.total_count}</th><th>{t.reserved}</th><th>{t.available}</th><th>+/-</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((s) => (
                  <tr key={s.product_type} className="border-t" data-testid={`jw-stock-${s.product_type}`}>
                    <td className="py-3 font-serif">{s.label}</td>
                    <td className="text-center font-mono">{s.grams_mg}</td>
                    <td className="text-center font-mono">{s.total_count}</td>
                    <td className="text-center font-mono text-amber-400">{s.reserved_count}</td>
                    <td className="text-center font-mono text-emerald-400">{s.available_count}</td>
                    <td className="text-center">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => stockDelta(s.product_type, -1)} data-testid={`jw-stock-minus-${s.product_type}`}>−1</Button>
                        <Button size="sm" className="h-8 rounded-full" onClick={() => stockDelta(s.product_type, +1)} data-testid={`jw-stock-plus-${s.product_type}`}>+1</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="prices" className="mt-5">
          <Card className="p-6 max-w-md space-y-4">
            <div><Label>{t.gram_price}</Label><Input type="number" value={prices.gram_price_kurus} onChange={(e) => setPrices({ ...prices, gram_price_kurus: e.target.value })} data-testid="jw-price-gram" /></div>
            <div><Label>{t.workmanship}</Label><Input type="number" value={prices.workmanship_bps} onChange={(e) => setPrices({ ...prices, workmanship_bps: e.target.value })} data-testid="jw-price-work" /></div>
            <Button onClick={savePrices} className="rounded-full" data-testid="jw-price-save">{t.save}</Button>
            <p className="text-xs text-muted-foreground">Girilen fiyat kuyumcuya bağlı bütün etkinliklerde kilit anındaki 90 sn için baz alınır.</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
